/**
 * The log terminal's state transitions, extracted so they can be tested.
 *
 * Same split as `FleetGrouping.ts`: this repository has no jsdom and no component renderer, and
 * the answer that has worked is to keep the decisions in a pure module and leave the JSX to
 * layout. Everything here is a decision — what the ring keeps, what the filter admits, whether
 * the tail is still being followed — and every one of them is a way the terminal could look
 * like it is working while showing the wrong thing.
 */

/** One line as `jmt_logging.publish` puts it on the wire and `topics.log_payload` validates it. */
export interface LogLine {
  service: string;
  level: string;
  ts: string;
  event: string;
  logger: string | null;
  correlation_id: string | null;
  fields: Record<string, unknown>;
  truncated?: boolean;
}

/** A row in the stream: a real line, or the server's own marker for a gap it created. */
export type NewRow = { kind: 'line'; line: LogLine } | { kind: 'gap'; count: number };

/**
 * A row on screen. `replay` is what distinguishes a line the terminal was *given* on subscribe from
 * one it *watched* arrive.
 *
 * 🔴 **Decided when the row arrives, not derived from its position.** `jmt_logging.publish` holds a
 * backfill ring on the far side of the demand gate (F-D82-3), so opening the terminal delivers up to
 * 500 lines that are already minutes old — and **changing the level re-subscribes**, which replays
 * again *after* live lines are already on screen. So replay is not a prefix and cannot be found by
 * scanning from the top; it is a property of each line, fixed at the moment it landed.
 */
export type Row = NewRow & { id: number; replay: boolean };

/**
 * Lines held. Bounded because this runs for hours during an incident and the tab must not grow
 * without limit; 1,500 is roughly nine bar closes of the full fleet at INFO.
 */
export const MAX_LINES = 1_500;

/** How close to the bottom still counts as "at the bottom", in px. */
export const TAIL_SLACK_PX = 24;

/**
 * Append `incoming` to `current`, keeping at most `MAX_LINES`.
 *
 * ⚠ **Oldest dropped, never newest.** During an incident the recent lines are the ones being
 * read, and a ring that discarded arrivals instead would go quiet exactly when it mattered —
 * while still looking like a working tail, because the rows already on screen stay there.
 */
export function appendRows(
  current: readonly Row[],
  incoming: readonly Row[],
  max: number = MAX_LINES,
): readonly Row[] {
  if (incoming.length === 0) return current;
  const next = current.concat(incoming);
  return next.length > max ? next.slice(next.length - max) : next;
}

/**
 * Where a "live from here" divider belongs: the index of each row that is live and whose predecessor
 * was replay.
 *
 * ⚠ **Every such transition, not just the first.** A level change re-subscribes and therefore
 * replays again, so a session can legitimately contain several replay blocks. One divider per
 * transition is the honest rendering; a single divider would silently mislabel every block after
 * the first.
 *
 * A leading replay block gets no divider *above* it — there is nothing above it to separate — and a
 * list that is entirely replay or entirely live gets none at all.
 */
export function boundaryIndices(rows: readonly Row[]): number[] {
  const out: number[] = [];
  // Local bindings rather than `!`: `noUncheckedIndexedAccess` is on for a reason, and an assertion
  // here would be the one place in this file that assumes the array is what the loop bound says.
  for (let i = 1; i < rows.length; i += 1) {
    const previous = rows[i - 1];
    const current = rows[i];
    if (previous?.replay && current && !current.replay) out.push(i);
  }
  return out;
}

/**
 * The rows a query admits.
 *
 * 🔴 **A gap marker always survives the filter.** It is not a log line and does not have fields
 * to match on; dropping it would hide the fact that lines went missing precisely when the
 * operator has narrowed to the ones they care about. A filtered view that silently omits "400
 * lines were dropped here" is worse than an unfiltered one.
 */
export function filterRows(rows: readonly Row[], query: string): readonly Row[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return rows;
  return rows.filter((row) => {
    if (row.kind === 'gap') return true;
    const { line } = row;
    return (
      line.event.toLowerCase().includes(needle) ||
      line.service.toLowerCase().includes(needle) ||
      (line.logger ?? '').toLowerCase().includes(needle) ||
      formatFields(line.fields).toLowerCase().includes(needle)
    );
  });
}

/**
 * Whether a scroll position still counts as following the tail.
 *
 * The slack matters: a stream that grows while the browser is mid-scroll leaves the position a
 * few pixels short of the bottom, and an exact comparison would drop out of follow mode on its
 * own — which is the paused indicator appearing for no reason the operator can see, repeatedly.
 */
export function isAtBottom(
  metrics: { scrollHeight: number; scrollTop: number; clientHeight: number },
  slack: number = TAIL_SLACK_PX,
): boolean {
  return metrics.scrollHeight - metrics.scrollTop - metrics.clientHeight <= slack;
}

/*
 * `clockOf` lived here and rendered UTC by slicing the ISO string. It moved to
 * `ui/timestamps.ts` as `preciseClockOf`, which takes the mode — `UI-Design-Spec.md` line 120's
 * toggle. Recorded rather than deleted silently: a string slice cannot honour a mode, so the move
 * was the fix and not a tidy-up.
 */

/**
 * The structured fields, flattened to `key=value` pairs.
 *
 * One string rather than an element per pair: at 1,500 rows the difference is thousands of DOM
 * nodes, and this is the part of a row nobody scans — it is read once, on the line that turned
 * out to matter. `null` and `undefined` are omitted rather than printed, because `reason=null`
 * reads as a value the system chose and it is the absence of one.
 */
export function formatFields(fields: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(fields ?? {})) {
    if (value === null || value === undefined) continue;
    parts.push(`${key}=${typeof value === 'object' ? JSON.stringify(value) : String(value)}`);
  }
  return parts.join('  ');
}

/** The topic a minimum level subscribes to. Mirrors `jmt_api.topics.log_topic`. */
export function topicFor(level: string): string {
  return `logs:${level}`;
}
